'use strict'

// Utils
import {getAppDomain} from '../utils/utils'
import {getSeoProductImages, findLowestPrice} from '../utils/product-utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

// Constants
import {BREADCRUMB_HOME_CATEGORY_NAME, HOME_HREF} from '../constants'

/**
 * Constructs a structured data list item for a category, used for SEO and breadcrumb navigation.
 *
 * @param {Object} category - The category object containing details about the category.
 * @param {number} position - The position of the category in the list, used for ordering.
 * @param {Function} buildUrl - A function to construct the URL for the category.
 * @param {boolean} isLastItem - A flag indicating whether this category is the last item in the list.
 * @returns {Object} A structured data object representing a list item, including type, position, name, and optionally an item URL.
 */
function buildListItem(category, position, buildUrl, isLastItem, isProduct = false) {
    const appOrigin = getAppDomain()
    // If it is the last category in the list
    if (isLastItem) {
        return {
            '@type': 'ListItem',
            position: position,
            name: category?.name
        }
    } else {
        return {
            '@type': 'ListItem',
            position: position,
            name:
                isProduct == true
                    ? category?.displayName || category?.name
                    : category?.name || category?.displayName,
            item: `${appOrigin}${buildUrl(`/category/${category.id}`)}`
        }
    }
}

/**
 * Constructs a JSON-LD representation of a breadcrumb trail for a given category tree.
 *
 * This function generates a structured data object in the format of a BreadcrumbList
 * as defined by Schema.org. It includes a home category and iterates over a provided
 * category tree to build a list of breadcrumb items, each represented as a ListItem.
 *
 * @param {Array} categoryTree - An array representing the hierarchy of categories to be included in the breadcrumb.
 * @param {Function} buildUrl - A function that generates a URL for a given category.
 * @returns {Object} A JSON-LD object representing the breadcrumb trail, suitable for embedding in a webpage.
 */
export function buildBreadcrumbLDJSON(categoryTree, buildUrl) {
    const appOrigin = getAppDomain()

    const itemListElement = [
        {
            '@type': 'ListItem',
            position: 1,
            name: BREADCRUMB_HOME_CATEGORY_NAME,
            item: appOrigin
        }
    ]
    const ldJSON = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: itemListElement
    }

    // Iterate over categories one by one and prepare structured data
    categoryTree.forEach((category, index) => {
        const isLastItem = index === categoryTree.length - 1
        itemListElement.push(buildListItem(category, index + 2, buildUrl, isLastItem, false))
    })

    ldJSON.itemListElement = itemListElement

    return ldJSON
}

/**
 * Constructs a structured data object in JSON-LD format for a product, suitable for SEO purposes.
 *
 * This function builds a JSON-LD object representing a product, including details such as name, image,
 * description, SKU, brand, offers, and shipping details. The structured data is used to enhance the
 * visibility of the product in search engine results by providing detailed product information.
 *
 * @param {Object} seoProduct - The product object containing SEO-related details. It should include properties
 *                              such as name, longDescription, type, master, id, inventory, and currency.
 * @param {string} siteID - The identifier for the site, used to determine shipping details and currency.
 * @param {Function} buildUrl - A function that constructs a URL for the product page based on the product ID.
 * @param {string} appOrigin - The origin URL of the application, used as the base for constructing product URLs.
 * @param {string} productId - The unique identifier for the product.
 * @returns {Object} A JSON-LD object representing the product, including its structured data for SEO.
 */
export function buildProductLDJSON(seoProduct, siteID, buildUrl, appOrigin, productId) {
    let lsJson = {}
    if (!seoProduct) {
        return lsJson
    }

    let shippingDetails = {
        value: siteID === 'VBQ-EU' ? 0 : 12,
        currency: siteID === 'VBQ-EU' ? 'EUR' : 'USD',
        addressCountry:
            siteID === 'VBQ-EU'
                ? [
                      'UK',
                      'FR',
                      'BE',
                      'DE',
                      'LU',
                      'NL',
                      'AT',
                      'CH',
                      'IT',
                      'ES',
                      'BG',
                      'HR',
                      'DK',
                      'EE',
                      'FI',
                      'HU',
                      'IE',
                      'LV',
                      'LT',
                      'PL',
                      'PT',
                      'SK',
                      'SI',
                      'SE'
                  ]
                : 'US'
    }

    const seoByProductId = Object.fromEntries(
        (seoProduct.c_variationGroupsSeo || []).map((item) => [item.productId, item])
    )

    const variationGroups = (seoProduct.variationGroups || []).map((vg) => ({
        ...vg,
        groupName: seoByProductId[vg.productId]?.groupName || '',
        groupColor: seoByProductId[vg.productId]?.groupColor || '',
        groupColorRegroupment: seoByProductId[vg.productId]?.groupColorRegroupment || '',
        groupIsOrderable: seoByProductId[vg.productId]?.groupIsOrderable || false,
        groupMinPrice: seoByProductId[vg.productId]?.groupMinPrice || 0
    }))

    let variantsArray = []
    variationGroups.forEach((variationGroup) => {
        let formattedColorLabel =
            variationGroup.groupColor.charAt(0).toUpperCase() +
            variationGroup.groupColor.slice(1).toLowerCase()
        let variantJson = {
            '@type': 'Product',
            sku: variationGroup.productId,
            name: variationGroup.groupName + ' ' + (variationGroup.groupColorRegroupment || ''),
            color:
                formattedColorLabel
                    .toLowerCase()
                    .indexOf(variationGroup.groupColorRegroupment.toLowerCase()) != -1
                    ? formattedColorLabel
                    : variationGroup.groupColorRegroupment + ' ' + formattedColorLabel,
            url: `${appOrigin}${buildUrl(`/product/${variationGroup.productId}`)}`,
            image: getSeoProductImages(seoProduct, variationGroup),
            offers: {
                '@type': 'Offer',
                url: `${appOrigin}${buildUrl(`/product/${variationGroup.productId}`)}`,
                availability: variationGroup.groupIsOrderable ? 'https://schema.org/InStock' : '',
                price: variationGroup.groupMinPrice,
                priceCurrency: seoProduct?.currency,
                priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 10))
                    .toISOString()
                    .split('T')[0],
                itemCondition: 'https://schema.org/NewCondition',
                seller: {
                    name: 'Vilebrequin',
                    '@type': 'Organization'
                },
                shippingDetails: {
                    '@type': 'OfferShippingDetails',
                    shippingRate: {
                        '@type': 'MonetaryAmount',
                        value: shippingDetails?.value,
                        currency: shippingDetails?.currency
                    },
                    shippingDestination: {
                        '@type': 'DefinedRegion',
                        addressCountry: shippingDetails?.addressCountry
                    }
                }
            }
        }

        variantsArray.push(variantJson)
    })

    lsJson = {
        '@context': 'https://schema.org',
        '@type': 'ProductGroup',
        name: seoProduct?.name,
        description:
            (seoProduct?.longDescription &&
                seoProduct?.longDescription
                    // eslint-disable-next-line no-useless-escape
                    ?.replace(/\"/g, '\\"')
                    ?.replace(/<.*?>/g, '')
                    ?.replace(/<\/.*?>/g, '')) ||
            '',
        productGroupID: seoProduct?.master?.masterId,
        variesBy: ['https://schema.org/color'],
        hasVariant: variantsArray,
        brand: {
            '@type': 'Brand',
            name: 'Vilebrequin'
        }
    }

    return lsJson
}

/**
 * Parses a JSON string to return structured data for the homepage.
 *
 * This function attempts to parse the provided JSON string and return the resulting object.
 * If parsing fails, it logs an error message to the console and returns undefined.
 *
 * @param {string} data - The JSON string representing the structured data for the homepage.
 * @returns {Object|undefined} The parsed structured data object if successful, otherwise undefined.
 */
export function getHomepageStructuredData(data) {
    const appOrigin = getAppDomain()
    try {
        const jsonParsed = JSON.parse(data)
        if (jsonParsed.logo) {
            jsonParsed.logo = appOrigin + getAssetUrl('static/img/logo-retina.png')
        }
        return jsonParsed
    } catch (error) {
        console.error('Error parsing structuredData:', error)
    }

    return
}

/**
 * Constructs a structured data object representing a breadcrumb trail for a product detail page (PDP).
 * This object is formatted according to the Schema.org BreadcrumbList specification.
 *
 * @param {Object} product - The product object containing details about the product.
 * @param {Array} productCategoryTree - An array representing the hierarchy of categories for the product.
 * @param {Function} buildUrl - A function that generates URLs for category items.
 * @returns {Object} A structured data object in JSON-LD format representing the breadcrumb trail.
 *
 * @throws Will log an error message to the console if an error occurs during the construction of the breadcrumb list.
 */
export function buildPDPBreadcrumbs(product, productCategoryTree, buildUrl) {
    const ldJSON = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: []
    }

    const itemListElement = []
    try {
        const appOrigin = getAppDomain()

        itemListElement.push({
            '@type': 'ListItem',
            position: 1,
            name: BREADCRUMB_HOME_CATEGORY_NAME,
            item: appOrigin + buildUrl(HOME_HREF)
        })

        // Iterate over categories one by one and prepare structured data
        productCategoryTree?.forEach((category, index) => {
            itemListElement.push(buildListItem(category, index + 2, buildUrl, false, true))
        })

        // Add the product as the last item
        itemListElement.push({
            '@type': 'ListItem',
            position: itemListElement.length + 1, // This should be position 5
            name: product?.name
        })

        ldJSON.itemListElement = itemListElement
    } catch (error) {
        ldJSON.itemListElement = []
    }

    return ldJSON
}

/**
 * Generates structured data for a breadcrumb list representing the store navigation path.
 * This data is formatted according to the Schema.org specifications for BreadcrumbList.
 *
 * @returns {Object} An object containing structured data for a breadcrumb list, including:
 *   - '@context': The context URL for Schema.org.
 *   - '@type': The type of structured data, which is 'BreadcrumbList'.
 *   - itemListElement: An array of list items, each representing a step in the breadcrumb trail.
 *     - Each list item includes:
 *       - '@type': The type of the list item, which is 'ListItem'.
 *       - position: The position of the list item in the breadcrumb trail.
 *       - name: The name of the breadcrumb step.
 *       - item: (Optional) The URL associated with the breadcrumb step, if applicable.
 */
export function buildStoresBreadcrumbs(buildUrl) {
    const appOrigin = getAppDomain()

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: BREADCRUMB_HOME_CATEGORY_NAME,
                item: appOrigin + buildUrl(HOME_HREF)
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'Stores'
            }
        ]
    }
}

/**
 * Constructs a structured data object representing breadcrumbs for a webpage.
 * This object follows the schema.org BreadcrumbList format, which is useful for SEO purposes.
 *
 * @param {Object} currentPage - The current page object, containing at least an 'id' and 'name' property.
 * @param {Object} homePage - The home page object, containing at least an 'id' and 'name' property.
 * @param {string} homeUrl - The URL of the home page.
 * @param {Function} buildUrl - A function that constructs URLs based on given paths.
 * @returns {Object} A structured data object representing the breadcrumb list. Returns an empty object if any of the required parameters are missing.
 */
export function buildContentBreadcrumbs(currentPage, homePage, homeUrl, buildUrl) {
    const appOrigin = getAppDomain()

    try {
        if (!currentPage || !homePage || !homeUrl) {
            return {}
        }

        const itemListElement = [
            {
                '@type': 'ListItem',
                position: 1,
                name: BREADCRUMB_HOME_CATEGORY_NAME,
                item: appOrigin + buildUrl(HOME_HREF)
            }
        ]

        // Conditionally add the 'item' property to the second ListItem
        const secondListItem = {
            '@type': 'ListItem',
            position: 2,
            name: homePage.name
        }

        if (currentPage.id !== homePage.id) {
            secondListItem.item = `${appOrigin}${homeUrl}`
            itemListElement.push(secondListItem)
            itemListElement.push({
                '@type': 'ListItem',
                position: 3,
                name: currentPage.name
            })
        } else {
            itemListElement.push(secondListItem)
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: itemListElement
        }
    } catch (error) {
        return {}
    }
}
